// UI-SPEC §"Generic error fallback" — title "Algo deu errado." + body
// "Tente recarregar a página. Se o problema persistir, fale com o suporte."
// + "Recarregar" button + linha condicional "Código: {digest}".
//
// T-01-05-03: do NOT render the error's message or its stack trace in the
// UI. The digest is the ONLY identifier safe to render.
//
// Where the error is actually recorded (Phase 265 / WR-08 — the previous comment
// here got this wrong and it costs real debugging time): this is a `'use client'`
// boundary, so the `useEffect` below runs ONLY after hydration, in the BROWSER.
// Its `console.error` lands in the user's devtools — never in the container's
// stdout, never in Pino. Grepping the app logs for `[app/error]` will find
// nothing, and that absence means nothing.
// The server-side record DOES exist, but Next emits it — it logs the original
// error with its digest when it serialises the boundary. `error.digest` is the
// handle that ties the two sides together: the code the user reads off the screen
// is the same string to grep for in the app logs.
'use client';

import { RefreshCcw } from 'lucide-react';
import { useEffect } from 'react';
import { Button } from '@/shared/ui/components/button';

export default function RouteError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Migalha de devtools no NAVEGADOR — não é o log do servidor (ver o cabeçalho).
    // O registro server-side quem emite é o próprio Next, com o mesmo `error.digest`.
    console.error('[app/error]', error);
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <main className="w-full max-w-md text-center flex flex-col items-center gap-6">
        <h1 className="font-serif text-3xl font-semibold leading-tight tracking-tight text-foreground">
          Algo deu errado.
        </h1>
        <p className="text-base text-muted-foreground leading-normal">
          Tente recarregar a página. Se o problema persistir, fale com o suporte.
        </p>
        <Button onClick={reset}>
          <RefreshCcw className="h-4 w-4" strokeWidth={1.75} />
          Recarregar
        </Button>
        {error.digest ? (
          <p className="text-sm text-muted-foreground">Código: {error.digest}</p>
        ) : null}
      </main>
    </div>
  );
}
